import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface AccessibleCompany {
  company_id: string;
  granted_at: string;
  granted_by: string;
  company: {
    id: string;
    name: string;
  };
}

export const useInterviewerAccess = () => {
  const { user, isTenantInterviewer, isLoading: authLoading } = useAuth();
  const [accessibleCompanies, setAccessibleCompanies] = useState<AccessibleCompany[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<string>('');

  const fetchAccessibleCompanies = useCallback(async () => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('useInterviewerAccess: Auth still loading, waiting...');
      return;
    }

    // Create a unique key for this fetch attempt
    const fetchKey = `${user?.id}-${isTenantInterviewer}`;

    if (!user) {
      // No user yet - keep null to indicate no data fetched
      if (lastFetchRef.current !== fetchKey) {
        console.log('useInterviewerAccess: No user yet, keeping null');
        lastFetchRef.current = fetchKey;
      }
      setIsLoading(false);
      return;
    }

    if (!isTenantInterviewer) {
      // User exists but is not tenant_interviewer - they don't need company access
      if (lastFetchRef.current !== fetchKey) {
        console.log('useInterviewerAccess: User is not tenant_interviewer, setting empty array');
        lastFetchRef.current = fetchKey;
      }
      setAccessibleCompanies([]);
      setIsLoading(false);
      return;
    }

    // Skip if this is the same request we just made
    if (lastFetchRef.current === fetchKey) {
      return;
    }

    try {
      setIsLoading(true);
      lastFetchRef.current = fetchKey;

      const { data, error } = await supabase
        .from('interviewer_company_access')
        .select(
          `
          company_id,
          created_at,
          granted_by,
          companies (
            id,
            name
          )
        `
        )
        .eq('user_id', user!.id);

      if (error) {
        console.error('useInterviewerAccess: Query error:', error);
        throw error;
      }

      console.log('useInterviewerAccess: Raw data from query:', data);

      // Transform the data to match the expected structure
      const transformedData = (data || []).map(item => ({
        ...item,
        granted_at: item.created_at,
        company: item.companies,
      }));

      console.log('useInterviewerAccess: Transformed data:', transformedData);
      setAccessibleCompanies(transformedData);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching accessible companies:', err);
      setAccessibleCompanies([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, isTenantInterviewer, authLoading]);

  useEffect(() => {
    // Only log when there's a meaningful change
    if (user?.id && isTenantInterviewer) {
      console.log(
        'useInterviewerAccess: Fetching for interviewer:',
        user?.id,
        'authLoading:',
        authLoading
      );
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the fetch to avoid rapid successive calls
    timeoutRef.current = setTimeout(() => {
      fetchAccessibleCompanies();
    }, 100);

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fetchAccessibleCompanies]);

  const hasAccessToCompany = useCallback(
    (companyId: string): boolean => {
      if (!isTenantInterviewer) return true; // Non-interviewers have access to all companies
      if (accessibleCompanies === null) return false; // Haven't loaded yet
      return accessibleCompanies.some(access => access.company_id === companyId);
    },
    [isTenantInterviewer, accessibleCompanies]
  );

  const getAccessibleCompanyIds = useCallback((): string[] => {
    if (!isTenantInterviewer) {
      return [];
    }
    if (accessibleCompanies === null) {
      return []; // Haven't loaded yet, return empty
    }
    const ids = accessibleCompanies.map(access => access.company_id);
    return ids;
  }, [isTenantInterviewer, accessibleCompanies]);

  const memoizedValue = useMemo(
    () => ({
      accessibleCompanies,
      isLoading,
      hasData: accessibleCompanies !== null,
      error,
      hasAccessToCompany,
      getAccessibleCompanyIds,
      refetch: fetchAccessibleCompanies,
    }),
    [
      accessibleCompanies,
      isLoading,
      error,
      hasAccessToCompany,
      getAccessibleCompanyIds,
      fetchAccessibleCompanies,
    ]
  );

  return memoizedValue;
};
